/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactNode} from 'react';
import Breadcrumbs, {BreadcrumbsProps} from '@material-ui/core/Breadcrumbs';
import {makeStyles} from '@material-ui/core/styles';
import omit from 'lodash.omit';
import {GenericObject} from '@ion/core/src/types/entities';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {Color} from '../../types/types';

// defines the prop definitions for the Breadcrumb
export interface Breadcrumbs extends BreadcrumbsProps {
  children: ReactNode;
  icon?: JSX.Element;
  iconClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  height?: string;
  breadcrumbClass?: string;
  style?: GenericObject;
  color?: Color;
  maxItems?: number;
  itemsAfterCollapse?: number;
  itemsBeforeCollapse?: number;
}

// defines the styles used by the Breadcrumbs
export const useStyles = makeStyles(theme => ({
  breadcrumbRoot : {
    display    : 'flex',
    alignItems : 'center',
    minHeight  : ({height}: {height?: string}) => height?.toString() || '70px',
    maxHeight  : '90px',
    '& span'   : {
      display : 'flex'
    },
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  breadcrumbIcon : {
    '&:hover' : {
      cursor : 'pointer'
    }
  }
}));

/**
 * @Breadcrumb
 *
 * Defines the Breadcrumb component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
 *   <Breadcrumb icon={} iconClick={}>
 *     {children}
 *   </Breadcrumb>
 * ```
 */
export const Breadcrumb = ({
  children,
  icon,
  iconClick,
  height,
  color,
  style,
  breadcrumbClass = '',
  maxItems = 3,
  itemsAfterCollapse = 1,
  itemsBeforeCollapse = 1,
  ...props
}: Breadcrumbs) => {
  const styleProps = {height, key: undefined};
  const classes = useStyles(styleProps);
  const breadcrumbsProps = children ? props : omit(props, ['classes']);
  return (
    <div style={style} className={`${classes.breadcrumbRoot} ${breadcrumbClass}`}>
      {icon &&
        <IconButton style={{all: 'unset', cursor: 'pointer'}}
          onClick={iconClick}
          color={color}
          icon={icon} />}
      <Breadcrumbs maxItems={maxItems}
        aria-label='breadcrumb'
        itemsAfterCollapse={itemsAfterCollapse}
        itemsBeforeCollapse={itemsBeforeCollapse}
        {...breadcrumbsProps}>
        {children}
      </Breadcrumbs>
    </div>
  );
};

export default Breadcrumb;
